import { Button, Card, Stack, Text, Title, Badge, Blockquote } from "@mantine/core";
import { IconBulb, IconArrowRight } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useProjectInsights } from "@/hooks/useProjectInsights";

interface AIInsightsPanelProps {
  projectId: string;
}

export function AIInsightsPanel({ projectId }: AIInsightsPanelProps) {
  const navigate = useNavigate();
  const insights = useProjectInsights(projectId);

  // Don't show if no data
  if (!insights.hasData || !insights.primaryInsight) {
    return null;
  }

  const { primaryInsight, recommendation } = insights;

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical":
        return "red";
      case "warning":
        return "yellow";
      case "info":
        return "blue";
      default:
        return "gray";
    }
  };

  const handleSeeAnalysis = () => {
    // Navigate to Cost Dashboard for detailed analysis
    navigate({ to: "/projects/$projectId/cost-dashboard", params: { projectId } } as any);
  };

  return (
    <Card withBorder padding="lg" style={{ background: "var(--mantine-color-blue-0)" }}>
      <Stack gap="md">
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "var(--mantine-color-blue-6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconBulb size={24} color="white" />
          </div>
          <div style={{ flex: 1 }}>
            <Title order={4}>KABLAN INSIGHTS</Title>
            <Text size="xs" c="dimmed">
              AI-Powered Analysis
            </Text>
          </div>
          <Badge color={getSeverityColor(primaryInsight.severity)} variant="light">
            {primaryInsight.severity.toUpperCase()}
          </Badge>
        </div>

        {/* Primary Insight */}
        <Blockquote
          color={getSeverityColor(primaryInsight.severity)}
          icon={null}
          style={{
            background: "white",
            borderLeftWidth: 4,
          }}
        >
          <Text size="sm" fw={500} mb="xs">
            {primaryInsight.title}
          </Text>
          <Text size="sm" c="dimmed">
            {primaryInsight.description}
          </Text>
        </Blockquote>

        {/* Recommendation */}
        {recommendation && (
          <div>
            <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="xs">
              Recommendation
            </Text>
            <Text size="sm">{recommendation}</Text>
          </div>
        )}

        {/* Action Button */}
        <Button
          variant="light"
          rightSection={<IconArrowRight size={16} />}
          onClick={handleSeeAnalysis}
          fullWidth
        >
          See Detailed Analysis
        </Button>
      </Stack>
    </Card>
  );
}
